import React, { useState, useEffect, useCallback } from 'react';
import AdminNav from '../components/AdminNav';
import {
  getParkingAttendants, addParkingAttendant, updateParkingAttendant, deleteParkingAttendant,
  getCongregations,
} from '../services/firebase';
import './Admin.css';

const DAYS = [
  { key: 'thu', label: 'Thu Apr 9',  field: 'thuAvailability' },
  { key: 'fri', label: 'Fri Apr 10', field: 'friAvailability' },
  { key: 'sat', label: 'Sat Apr 11', field: 'satAvailability' },
  { key: 'sun', label: 'Sun Apr 12', field: 'sunAvailability' },
];

const AVAIL_OPTIONS = [
  { value: '',              label: '—'           },
  { value: 'Not Available', label: 'Not Available' },
  { value: 'Half Day AM',   label: 'Half Day AM'   },
  { value: 'Half Day PM',   label: 'Half Day PM'   },
  { value: 'Anytime',       label: 'Anytime'       },
];

const AVAIL_COLORS = {
  '':              { bg: '#f1f5f9', color: '#64748b' },
  'Not Available': { bg: '#fef2f2', color: '#dc2626' },
  'Half Day AM':   { bg: '#fff7ed', color: '#c2410c' },
  'Half Day PM':   { bg: '#fef9c3', color: '#ca8a04' },
  'Anytime':       { bg: '#f0fdf4', color: '#16a34a' },
};

const BLANK = {
  firstName: '', lastName: '', phone: '', email: '', congregation: '', notes: '',
  thuAvailability: '', friAvailability: '', satAvailability: '', sunAvailability: '',
};

function ParkingAttendants({ onLogout }) {
  const [attendants, setAttendants] = useState([]);
  const [congregations, setCongregations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newData, setNewData] = useState({ ...BLANK });
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [atts, congs] = await Promise.all([getParkingAttendants(), getCongregations()]);
    setAttendants(atts.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || '')));
    setCongregations(congs.filter(c => c.active).map(c => c.name).sort());
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = async () => {
    if (!newData.firstName.trim() || !newData.lastName.trim()) return;
    setSaving(true);
    await addParkingAttendant(newData);
    setNewData({ ...BLANK });
    setShowAddForm(false);
    await loadData();
    setSaving(false);
  };

  const handleEdit = (att) => {
    setEditingId(att.id);
    setEditData({ ...att });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    const { id, ...rest } = editData;
    await updateParkingAttendant(editingId, rest);
    setEditingId(null);
    await loadData();
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this parking attendant?')) return;
    await deleteParkingAttendant(id);
    await loadData();
  };

  const filteredAttendants = attendants.filter(a =>
    `${a.firstName} ${a.lastName} ${a.congregation}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="admin-container"><AdminNav onLogout={onLogout} /><div className="loading">Loading…</div></div>;

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Parking Attendants</h1>
        <p className="page-subtitle">Manage volunteers assigned to parking attendant duties at the Kingdom Hall.</p>
      </header>

      <div className="scheduling-content">
        {saving && <div className="save-indicator">💾 Saving…</div>}

        {/* Toolbar */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input
            type="text"
            className="search-input"
            placeholder="Search attendants…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ maxWidth: '320px' }}
          />
          <button className="btn btn-primary" onClick={() => { setShowAddForm(true); setNewData({ ...BLANK }); }}>
            + Add Attendant
          </button>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <div className="location-section" style={{ marginBottom: '24px' }}>
            <div className="shift-header" style={{ padding: '14px 20px', background: '#f0f7ff', borderRadius: '8px 8px 0 0' }}>
              <strong>New Parking Attendant</strong>
            </div>
            <div style={{ padding: '20px' }}>
              <AttendantForm data={newData} onChange={(f, v) => setNewData(prev => ({ ...prev, [f]: v }))} congregations={congregations} />
              <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>Save</button>
                <button className="btn btn-secondary" onClick={() => setShowAddForm(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Attendant List */}
        {filteredAttendants.length === 0 ? (
          <div className="no-data">{attendants.length === 0 ? 'No attendants added yet.' : 'No results match your search.'}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {filteredAttendants.map(att => (
              <div key={att.id} className="location-section">
                {editingId === att.id ? (
                  <div style={{ padding: '20px' }}>
                    <AttendantForm data={editData} onChange={(f, v) => setEditData(prev => ({ ...prev, [f]: v }))} congregations={congregations} />
                    <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                      <button className="btn btn-primary" onClick={handleSaveEdit} disabled={saving}>Save</button>
                      <button className="btn btn-secondary" onClick={() => setEditingId(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '16px', color: '#1e293b' }}>
                          {att.lastName}, {att.firstName}
                        </div>
                        <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                          {att.congregation || 'No congregation'}
                          {att.phone && <> · <a href={`tel:${att.phone}`} style={{ color: '#667eea' }}>{att.phone}</a></>}
                          {att.email && <> · {att.email}</>}
                        </div>
                        {att.notes && <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '4px', fontStyle: 'italic' }}>{att.notes}</div>}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-secondary" style={{ padding: '5px 12px', fontSize: '13px' }} onClick={() => handleEdit(att)}>Edit</button>
                        <button className="btn-delete" onClick={() => handleDelete(att.id)}>×</button>
                      </div>
                    </div>

                    {/* Availability pills */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {DAYS.map(d => {
                        const val = att[d.field] || '';
                        const colors = AVAIL_COLORS[val] || AVAIL_COLORS[''];
                        return (
                          <span key={d.key} style={{ fontSize: '12px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px', background: colors.bg, color: colors.color }}>
                            {d.label}: {val || '—'}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AttendantForm({ data, onChange, congregations }) {
  return (
    <div>
      <div className="roster-edit-grid" style={{ marginBottom: '16px' }}>
        <label>First Name *<input value={data.firstName} onChange={e => onChange('firstName', e.target.value)} /></label>
        <label>Last Name *<input value={data.lastName} onChange={e => onChange('lastName', e.target.value)} /></label>
        <label>Phone<input type="tel" value={data.phone} onChange={e => onChange('phone', e.target.value)} /></label>
        <label>Email<input type="email" value={data.email} onChange={e => onChange('email', e.target.value)} /></label>
        <label>Congregation
          <select value={data.congregation} onChange={e => onChange('congregation', e.target.value)}>
            <option value="">Select…</option>
            {congregations.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>Notes<input value={data.notes} onChange={e => onChange('notes', e.target.value)} placeholder="Optional" /></label>
      </div>

      <div style={{ marginTop: '12px' }}>
        <div style={{ fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '10px' }}>Availability</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
          {DAYS.map(d => (
            <div key={d.key}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>{d.label}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {AVAIL_OPTIONS.map(opt => {
                  const active = (data[d.field] || '') === opt.value;
                  const colors = AVAIL_COLORS[opt.value];
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => onChange(d.field, opt.value)}
                      style={{
                        padding: '3px 9px', fontSize: '12px', borderRadius: '10px', cursor: 'pointer',
                        border: active ? '2px solid currentColor' : '1px solid #d1d5db',
                        background: active ? colors.bg : 'white',
                        color: active ? colors.color : '#64748b',
                        fontWeight: active ? 700 : 400,
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ParkingAttendants;
